<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { useManufacturersStore } from "~/stores/manufacturers";

// -----------------------
// composables
// -----------------------
const manufacturersStore = useManufacturersStore();
const route = useRoute();

// -----------------------
// middleware
// -----------------------
definePageMeta({
  middleware: [
    async function (to) {
      // For TNF we don't need the manufacturers page, return 404
      if (useRuntimeConfig().public.theme.site === "tnf") {
        throw createError({
          statusCode: 404,
        });
      }
      if (to.params.slug) {
        let manufacturers = null;

        try {
          manufacturers = await $fetch(`/api/meilisearch/manufacturers`, {
            method: "POST",
            body: {
              slug: to.params.slug,
            },
          });
        } catch (error) {
          Sentry.captureEvent({
            message: `Failed to fetch CMS page from redis`,
            level: "error",
            extra: {
              key: "ER-MN45",
              slug: to.params.slug,
              error,
            },
          });
        }

        if (Array.isArray(manufacturers) && manufacturers.length === 0) {
          throw createError({
            statusCode: 404,
          });
        }
      }
    },
  ],
});

// -----------------------
// reactive properties
// -----------------------
const isList = computed(() => !route.params.slug);

// -----------------------
// Fetch manufacturers (SSR Safe)
// -----------------------
const { data: manufacturers } = await useAsyncData(
  "manufacturers",
  async () => {
    try {
      const manufacturers = await $fetch(`/api/meilisearch/manufacturers`, {
        method: "POST",
        body: {
          limit: 9999,
          sort: "name:asc",
        },
      });

      return manufacturers ?? [];
    } catch (error) {
      Sentry.captureEvent({
        message: `Failed to fetch manufacturers`,
        level: "error",
        extra: {
          key: "ER-MLKB",
          error,
        },
      });

      return [];
    }
  },
);

// -----------------------
// Sync store (reactively)
// -----------------------
watchEffect(() => {
  if (manufacturers.value) {
    manufacturersStore.manufacturers = manufacturers.value;
  }
});
</script>

<template>
  <ManufacturersList v-if="isList"></ManufacturersList>
  <ManufacturersPage v-else></ManufacturersPage>
</template>
